import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { useModalShareTaxInformationWithPayers } from "../../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";
import { ExtendedRequirementTableItemData } from "../../../types";
import { getIsGeneralRequirement } from "../../../utils";
import { getPayoutMethodCustomRequirement } from "./getPayoutMethodCustomRequirement";
import { getRegistrationCustomRequirement } from "./getRegistrationCustomRequirement";
import { getShareConsentCustomRequirement } from "./getShareConsentCustomRequirement";
import { getTaxVerificationCustomRequirement } from "./getTaxVerificationCustomRequirement";

type GeneralRequirements = Partial<
  Record<RequirementType, IRequirementResponse[]>
>;

export function usePayerRequirements(requirements: IRequirementResponse[]) {
  const modalFormW9Consent = useModalShareTaxInformationWithPayers();

  const clientsRequirements = useMemo(() => {
    // Filter out general requirements
    const normalizedList: ExtendedRequirementTableItemData[] =
      requirements.filter(requirement => !getIsGeneralRequirement(requirement));

    // Group up general requirements
    const generalRequirements = groupGeneralRequirements(requirements);

    // Inject custom requirements projected from grouped general requirements
    injectCustomRequirements(normalizedList, generalRequirements, () => {
      modalFormW9Consent.open();
    });

    return normalizedList;
  }, [modalFormW9Consent, requirements]);

  return clientsRequirements;
}

function groupGeneralRequirements(requirements: IRequirementResponse[]) {
  const generalRequirementMap: GeneralRequirements = {};

  requirements.forEach(requirement => {
    if (!getIsGeneralRequirement(requirement)) {
      return;
    }

    const requirementType = requirement.requirementType;
    if (!generalRequirementMap[requirementType]) {
      generalRequirementMap[requirementType] = [];
    }
    generalRequirementMap[requirementType]!.push(requirement);
  });

  return generalRequirementMap;
}

function injectCustomRequirements(
  list: ExtendedRequirementTableItemData[],
  generalRequirements: GeneralRequirements,
  openConsentModal: () => void
) {
  (Object.keys(generalRequirements) as RequirementType[]).forEach(key => {
    switch (key) {
      case RequirementType.Registration:
        if (generalRequirements[key]!.length === 1) {
          // Don't group single requirement
          list.unshift(generalRequirements[key]![0]);
          break;
        }

        list.unshift(
          getRegistrationCustomRequirement(generalRequirements[key]!)
        );
        break;

      case RequirementType.TaxVerification:
        if (generalRequirements[key]!.length === 1) {
          // Don't group single requirement
          list.unshift(
            generalRequirements[key]![0],
            getShareConsentCustomRequirement(
              generalRequirements[key]!,
              openConsentModal
            )
          );

          break;
        }

        list.unshift(
          getTaxVerificationCustomRequirement(generalRequirements[key]!),
          getShareConsentCustomRequirement(
            generalRequirements[key]!,
            openConsentModal
          )
        );
        break;

      case RequirementType.PayoutMethod:
        if (generalRequirements[key]!.length === 1) {
          // Don't group single requirement
          list.unshift(generalRequirements[key]![0]);
          break;
        }

        list.unshift(
          getPayoutMethodCustomRequirement(generalRequirements[key]!)
        );
        break;
    }
  });
}
